import type { StyleRule } from '@archipro-design/aria';
import { pxToRem, pxArrayToRem } from '@archipro-design/aria';
import {
    boxClassName,
    captionClassName,
    videoPlayerClassName,
} from '@archipro-design/aria';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import {
    PADDING_LEFT_RIGHT_SECTION_MOBILE,
    PADDING_LEFT_RIGHT_SECTION,
} from '../../common/pagebuilder.style';
import { PROJECT_REVAMP_CAPTION_TEXT } from '../../common/image-caption/ImageCaption.style';

export const SingleVideoModule: StyleRule<{
    isSingleVideoParagraphModule?: boolean;
    isProjectRevamp?: boolean;
}> = ({ theme, isSingleVideoParagraphModule, isProjectRevamp }) => ({
    margin: '0 auto',
    width: '100%',

    padding: pxArrayToRem([0, PADDING_LEFT_RIGHT_SECTION_MOBILE]),

    [theme.screen.laptop]: {
        padding: pxArrayToRem([0, PADDING_LEFT_RIGHT_SECTION]),

        ...(isSingleVideoParagraphModule && {
            padding: '0 !important',
        }),

        // Project detail revamp (BC-4725): the standalone video is the same
        // centred box as the large single image module — the gallery content
        // width plus 0.25rem per side, capped at 114.5rem (1832px on the 1920
        // Figma frame — 44px viewport insets). The cap-plus-clamp keeps the
        // 744px–1024px band sane, where the mobile-scale 3.865vw root makes a
        // bare 114.5rem ~4x the viewport (see DetailSingleImageContainer).
        ...(isProjectRevamp &&
            !isSingleVideoParagraphModule && {
                padding: '0',
                width: 'calc(100% + 0.5rem)',
                maxWidth: '114.5rem',
                // The module root is a flex item of the renderer's wrapper —
                // stop it shrinking back to the wrapper width.
                flexShrink: 0,
                marginLeft: 'max(-0.25rem, calc((100% - 114.5rem) / 2))',
                marginRight: '0',
            }),
    },
});

export const AspectRatioBox: StyleRule<{
    dimension?: ColumnDimensionType;
    isSingleVideoParagraphModule?: boolean;
}> = ({ theme, dimension, isSingleVideoParagraphModule }) => ({
    position: 'relative',
    height: dimension?.mobileHeight ? pxToRem(dimension.mobileHeight) : '100%',
    width: '100%',
    margin: isSingleVideoParagraphModule ? '0' : '0 auto', // Left align for video paragraph module

    [theme.screen.laptop]: {
        overflow: 'hidden',
        ...(isSingleVideoParagraphModule && {
            maxWidth: '100%',
            width: pxToRem(1050), // Always 1050px width for video paragraph module (like landscape images)
            aspectRatio: '16/9', // Standard landscape video aspect ratio
            height: 'auto', // Let aspect ratio determine height
        }),

        ...(!isSingleVideoParagraphModule && {
            height: dimension?.height ? pxToRem(dimension.height) : '100%',
        }),
    },

    [`& .${videoPlayerClassName}`]: {
        width: '100%',
        height: '100%',

        [`& .${boxClassName}`]: {
            position: 'absolute',
        },
    },
});

export const CaptionVideo: StyleRule<{
    dimension?: ColumnDimensionType;
    isSingleVideoParagraphModule?: boolean;
    isProjectRevamp?: boolean;
}> = ({ theme, isSingleVideoParagraphModule, isProjectRevamp }) => ({
    margin: '0 auto',
    letterSpacing: '0.015em',
    width: '100%',
    fontSize: pxToRem(20),
    lineHeight: '1.2',
    color: '#161616',
    textAlign: 'left',
    overflowWrap: 'break-word',
    fontWeight: 450,

    [`& .${captionClassName}`]: {
        fontSize: pxToRem(22),
        lineHeight: '120%',
        letterSpacing: '0.015em',
        fontWeight: 450,
        color: '#161616',
        fontFamily: 'neue-haas-grotesk-display',
    },

    [theme.screen.laptop]: {
        ...(isSingleVideoParagraphModule && {
            margin: '0',
            maxWidth: '100%',
            width: pxToRem(1050), // Match the video width for video paragraph module
        }),

        // Project detail revamp (BC-4725): video captions share the same
        // Figma caption text style as the image modules.
        ...(isProjectRevamp && {
            [`& .${captionClassName}`]: PROJECT_REVAMP_CAPTION_TEXT,
        }),
    },
});
